import { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import type { InterviewMessage } from '../types/interview';

interface InterviewTranscriptProps {
  sessionId: string;
}

export default function InterviewTranscript({ sessionId }: InterviewTranscriptProps) {
  const { t } = useLanguage();
  const i = t.interview;
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState<InterviewMessage[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || transcript !== null) return;
    setLoading(true);
    fetch(`/api/interview?sessionId=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        setTranscript(data.transcript || []);
      })
      .catch(() => {
        setTranscript([]);
      })
      .finally(() => setLoading(false));
  }, [open, sessionId, transcript]);

  return (
    <div className="bg-white border border-cloud">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[44px] px-4 sm:px-5 py-3 text-left text-sm font-semibold text-slate uppercase tracking-wider hover:bg-mist transition-colors"
      >
        {i.transcriptTitle} {open ? '−' : '+'}
      </button>
      {open && (
        <div className="border-t border-cloud p-4 sm:p-5 max-h-[60vh] sm:max-h-80 overflow-y-auto space-y-3">
          {loading && <div className="text-sm text-ash">...</div>}
          {!loading && (!transcript || transcript.length === 0) && (
            <div className="text-sm text-ash italic">{i.transcriptEmpty}</div>
          )}
          {!loading &&
            transcript &&
            transcript.map((m) => (
              <div
                key={m.id}
                className={`text-sm ${
                  m.role === 'user' ? 'text-ink' : m.kind === 'clarification' ? 'text-slate italic' : 'text-slate'
                }`}
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-ash mb-0.5">
                  {m.role === 'user' ? 'You' : m.kind === 'clarification' ? 'Clarification' : 'Engine'}
                </div>
                <div className="leading-relaxed whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
